import React, { useEffect, useState } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider as MDSThemeProvider } from '@emotion/react';
import { StylesProvider } from '@material-ui/core/styles';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@material-ui/core';
import theme from '@marqvision/shared/src/library/theme';
import { ToastContainer } from '@marqvision/shared';
import { MDSThemeValue, MDSResetCSS } from '@marqvision/mds-v2';
import usePageview from '~/hooks/usePageview';
import useRouteNames from '~/hooks/useRouteNames';
import AccountPanel from './components/AccountPanel';
import PolicyPanel from './components/PolicyPanel';
import { LanguageLocalStorageIdentifier } from './hooks/useLanguage';
import { LanguageEnum } from './LocalizedStrings';
import { useSentryUserInfoUpdate } from './hooks/useSentryUserInfoUpdate';

polyfillCountryFlagEmojis();

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

export interface IGlobalActionContext {
  login: (callback?: () => void, forceRedirect?: boolean) => void;
  showPolicy: (type: 'terms' | 'privacy') => void;
}

export interface ILanguageContext {
  language: LanguageEnum;
  setLanguage: React.Dispatch<React.SetStateAction<LanguageEnum>>;
}

export const GlobalActionContext = React.createContext<IGlobalActionContext>({
  login: () => undefined,
  showPolicy: () => undefined,
});

export const LanguageContext = React.createContext<ILanguageContext>({
  language: LanguageEnum.English,
  setLanguage: () => undefined,
});

const useScrollTopByPath = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById('scrollLayout')?.scrollTo(0, 0);
  }, [pathname]);
};

const useHistoryControl = ({ language }: { language: LanguageEnum }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (language) {
      sessionStorage.setItem(LanguageLocalStorageIdentifier, language);

      // redirect
      const currentUrl = (() => {
        const urls = window.location.pathname.split('/');
        urls.splice(0, 1);

        if (Object.values(LanguageEnum).includes(urls[0] as LanguageEnum)) {
          urls.splice(0, 1);
        }

        return urls.join('/') + window.location.search;
      })();

      const newPath = `${language !== LanguageEnum.English ? `/${language}` : ''}/${currentUrl}`;
      navigate(newPath, { replace: true });
    }
  }, [navigate, language]);
};

const growthbook = new GrowthBook({
  apiHost: process.env.REACT_APP_GROWTHBOOK,
  clientKey: process.env.REACT_APP_GROWTHBOOK_KEY,
  enableDevMode: true,
});

const App = () => {
  const navigate = useNavigate();
  const { Home } = useRouteNames();

  const [language, setLanguage] = useState<LanguageEnum>(() => {
    /**
     * 초기 언어 우선순위
     * 1. URL 에 명시된 언어
     * 2. Session Storage 에 저장 되어있는 언어
     * 3. 브라우저 내장 객체의 언어
     * 4. 영어
     */
    if (window.location && window.location.pathname) {
      const urls = window.location.pathname.split('/');
      if (Object.values(LanguageEnum).includes(urls[1] as LanguageEnum)) {
        const initialLanguage = urls[1];

        return initialLanguage as LanguageEnum;
      }
    }

    return (
      (sessionStorage.getItem(LanguageLocalStorageIdentifier) as LanguageEnum) ||
      (navigator.language.substring(0, 2) as LanguageEnum) ||
      LanguageEnum.English
    );
  });
  const [isShowLogin, setIsShowLogin] = useState<boolean>(false);
  const [isShowPolicy, setIsShowPolicy] = useState<'terms' | 'privacy' | null>(null);
  const [loginCallback, setLoginCallback] = useState<() => void>();
  const [forceRedirect, setForceRedirect] = useState(false);

  const login = (callback?: () => void, forceRedirect?: boolean) => {
    setIsShowLogin(true);
    setLoginCallback(callback ? () => callback : undefined);
    if (forceRedirect) {
      setForceRedirect(true);
    }
  };
  const showPolicy = (type: 'terms' | 'privacy') => setIsShowPolicy(type);

  usePageview();
  useHistoryControl({ language });
  useSentryUserInfoUpdate();
  useScrollTopByPath();

  useEffect(() => {
    growthbook.loadFeatures();
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <MuiThemeProvider theme={{ ...theme, ...MDSThemeValue }}>
        <LanguageContext.Provider value={{ language, setLanguage }}>
          <GlobalActionContext.Provider value={{ login, showPolicy }}>
            <StylesProvider injectFirst>
              <Elements
                stripe={stripePromise}
                options={{
                  fonts: [
                    {
                      family: 'Visuelt-Medium',
                      src: "url(https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.8/dist/packages/pretendard/dist/web/variable/woff2/PretendardVariable.woff2) format('woff2')",
                      weight: '500',
                    },
                  ],
                }}
              >
                <CssBaseline />
                <MDSThemeProvider theme={MDSThemeValue}>
                  <MDSResetCSS />
                  <Outlet />
                </MDSThemeProvider>
              </Elements>
              <AccountPanel
                open={isShowLogin}
                onClose={(isLoggedIn) => {
                  setIsShowLogin(false);
                  if (isLoggedIn) {
                    loginCallback?.();
                  } else if (forceRedirect) {
                    navigate(Home);
                    setForceRedirect(false);
                  }
                }}
              />
              <PolicyPanel type={isShowPolicy} onClose={() => setIsShowPolicy(null)} />
              <ToastContainer />
            </StylesProvider>
          </GlobalActionContext.Provider>
        </LanguageContext.Provider>
      </MuiThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
    </GrowthBookProvider>
  );
};

export default App;
